/**
 * Workspace for /d/:id.
 *
 * Shared chrome: LiliaTopBar + TopBar (once), then the Flow canvas.
 * Thin top loading bar while the document boots.
 *
 * Until 2026-09-06 this mounted Flow or Block depending on ?mode=, and kept a
 * `seen` map so a surface stayed mounted once visited — switching back had to
 * be instant, and remounting Block was expensive. Block is gone, so there is
 * one surface, nothing to keep alive, and nothing to switch between.
 */
import { RememberPlace } from "@/components/navigation/RememberPlace";
import { useParams } from "react-router";
import { useIsFetching } from "@tanstack/react-query";
import {
  WorkspaceShellProvider,
  useWorkspaceShell,
} from "@/contexts/WorkspaceShellContext";
import { LiliaTopBar } from "@/components/template/lilia-shell/LiliaTopBar";
import { useMobileReader } from "@/hooks/useMobileMode";
import { TopBar } from "@/components/editor/TopBar";
import { SaveIndicator } from "@/components/editor/saveIndicator/SaveIndicator";
import { WorkspaceLoadingBar } from "@/components/editor/WorkspaceLoadingBar";
import EditorPage from "@/pages/EditorPage";

/**
 * The document's own controls. Compact chrome hosts them inside the shell bar
 * rather than on a row of their own: that row was carrying a second search
 * field and a second theme toggle, and a layout preset the pane tabs already
 * express. Design: lilia-docs/design-handoffs/2026-09-11-editor-chrome/ §4.
 */
function DocumentActions({ documentId }: { documentId: string }) {
  const { topBar, arrangement, setArrangement, openPalette } = useWorkspaceShell();
  if (!topBar) {
    // Minimal chrome while first surface boots.
    return (
      <TopBar
        compact
        documentId={documentId}
        title=""
        onTitleChange={() => {}}
        mode="visual"
        onModeChange={() => {}}
        saveStatus="idle"
        isDirty={false}
        onSaveNow={() => {}}
        documentLoading
        arrangement={arrangement}
        onArrangementChange={setArrangement}
        onOpenCommandPalette={openPalette}
      />
    );
  }

  return (
    <TopBar
      compact
      documentId={documentId}
      title={topBar.title}
      onTitleChange={topBar.onTitleChange}
      mode={topBar.mode ?? "visual"}
      onModeChange={topBar.onModeChange ?? (() => {})}
      saveStatus={topBar.saveStatus}
      isDirty={topBar.isDirty}
      onSaveNow={topBar.onSaveNow}
      lastSavedAt={topBar.lastSavedAt}
      isPublic={topBar.isPublic}
      shareLink={topBar.shareLink}
      shareSlug={topBar.shareSlug}
      ownerId={topBar.ownerId}
      presenceUsers={topBar.presenceUsers}
      onPasteLatex={topBar.onPasteLatex}
      validationIssueCount={topBar.validationIssueCount}
      onOpenPackages={topBar.onOpenPackages}
      onOpenSettings={topBar.onOpenSettings}
      document={topBar.document}
      documentLoading={topBar.documentLoading}
      documentReadOnly={topBar.documentReadOnly}
      arrangement={arrangement}
      onArrangementChange={setArrangement}
      onOpenCommandPalette={openPalette}
    />
  );
}

function WorkspaceBody({ id }: { id: string }) {
  const { openPalette, topBar } = useWorkspaceShell();
  // A phone reads the document in the mobile reader, which brings its own top bar.
  const mobileReader = useMobileReader();

  const docFetching = useIsFetching({ queryKey: ["document", id] }) > 0;
  const loadingBarActive = !topBar || !!topBar.documentLoading || docFetching;

  if (mobileReader) {
    return (
      <div className="relative h-[100dvh] w-screen overflow-hidden bg-[var(--lilia-bg)]">
        <WorkspaceLoadingBar active={loadingBarActive} />
        <EditorPage />
      </div>
    );
  }

  return (
    <div className="relative flex h-screen w-screen flex-col overflow-hidden bg-[var(--lilia-bg)]">
      <WorkspaceLoadingBar active={loadingBarActive} />
      <LiliaTopBar
        onOpenPalette={openPalette}
        titleAccessory={<SaveIndicator />}
        documentSlot={<DocumentActions documentId={id} />}
      />
      <div className="relative min-h-0 flex-1 overflow-hidden">
        <div
          className="absolute inset-0 flex flex-col"
          data-workspace-mode="flow"
          data-workspace-active="true"
        >
          <EditorPage />
        </div>
      </div>
    </div>
  );
}

export default function DocumentWorkspaceLayout() {
  const { id = "" } = useParams<{ id: string }>();

  return (
    <WorkspaceShellProvider documentId={id}>
      {/* The section you're reading, for Home's Continue. */}
      <RememberPlace docId={id} />
      <WorkspaceBody id={id} />
    </WorkspaceShellProvider>
  );
}
